import z from 'zod';
import { domainSchema, nameSchema, quantitySchema } from '../../validation';
import { partyRoleSchema, vehicleTypesSchema } from '../../common';
import { financingFleetTierSchema } from '../financing/financing.enums';
import { leadSourceMetaSchema, leadSourceSchema } from './lead.source.model';
import { leadFirstTouchSchema, leadLastTouchSchema } from './lead.first-touch.model';
import { billingRefSchema } from '../sales';

/**
 * Company-level metadata for a Lead, plus the origin (`source`) of the
 * lead itself.
 *
 * Per-person fields (`personName`, `phone`, `email`) used to live here;
 * they have moved to `Lead.people[]` (see {@link leadPersonSchema}) so
 * a single Lead can carry multiple contacts (decision maker, ops, etc.)
 * with one consistent shape across the public landing form, hunter
 * submissions, and admin manual entry.
 */
export const leadMetaSchema = z.object({
  // ─── company-level ─────────────────────────────────────────────────
  /** Name of the company / business the lead represents. */
  companyName: nameSchema.optional(),
  /**
   * What this account is, in network-role terms — carrier company,
   * broker, business customer, individual customer, freelance driver.
   * Multi-valued, because a carrier that also brokers is both.
   *
   * Named `accountType` and not `companyType` because most of what it
   * describes is not a company: `metaFromUser` fills it with `['driver']`
   * for a freelance owner-operator and `['individual_customer']` for a
   * private person.
   *
   * **The only place the buyer kind is stored.** The one-word
   * company / freelancer / individual reading used to live beside this
   * as a second scalar field, which meant two vocabularies for one fact
   * and no rule about which won. It is now derived on read by
   * `leadAccountKind()` in `@hero/core`, with `rolesForAccountKind()`
   * as the inverse for filters.
   */
  accountType: z.array(partyRoleSchema).optional(),
  /**
   * Rough fleet size the lead operates / needs — expressed as a
   * bracket (`lt_10`, `11_20`, ..., `gt_500`). Shares the same enum
   * as the financing profile (`financingFleetTierSchema`) so the same
   * question is only ever answered once across the product and
   * lenders can consume it directly if the lead converts and applies
   * for financing.
   *
   * The landing "Book a demo" form makes this required client-side;
   * the schema keeps it optional so admin / hunter flows can capture
   * a lead without knowing the fleet size yet.
   */
  fleetSize: financingFleetTierSchema.optional(),
  /**
   * Precise vehicle count, when someone gave an exact number rather
   * than a bracket — the admin form, or a signup merge.
   *
   * Named to match `Org.meta.estimatedVehicleTotal` (it was
   * `vehiclesCount`) so lead → Org conversion is a 1:1 copy instead of
   * a mapping someone has to remember. Coexists with {@link fleetSize}
   * rather than replacing it: `fleetTierFromCount` in `@hero/core`
   * derives the bracket from the number so the two can never disagree,
   * and the bracket stays authoritative when no exact number is known.
   */
  estimatedVehicleTotal: quantitySchema.optional(),
  /**
   * Expected orders per month, as an exact number.
   *
   * Same name as `Org.meta.estimatedOrdersPerMonth`, for the same
   * reason. Before this field the answer was captured as prose —
   * `buildOrgSignupDescription` wrote "Est. monthly orders: 40" into
   * {@link description} — which no filter, no segment, and no report
   * can read. The agent asks this question during onboarding, so
   * dropping the answer on the floor was asking for nothing.
   */
  estimatedOrdersPerMonth: quantitySchema.optional(),
  /** Vehicle types the lead operates / is interested in. */
  vehicleTypes: z.array(vehicleTypesSchema).optional(),
  /** Public website of the company. */
  website: domainSchema.optional(),
  /**
   * Free-form description of the lead's business + what they need
   * help with. Captured by the landing "Book a demo" form so the
   * sales team has context before the first call; also editable by
   * admins from the lead drawer.
   */
  description: z.string().optional(),

  // ─── origin / attribution ──────────────────────────────────────────
  /**
   * Which of our doors they came through — the landing site, a hunter,
   * a marketplace listing, an import.
   *
   * Required, and coarser than {@link firstTouch} on purpose: it is the
   * campaign-level bucket reports group by, and it survives on the leads
   * that have no first touch at all (a spreadsheet import).
   */
  source: leadSourceSchema,
  /**
   * The very first contact between us and this lead — when, how, on what
   * surface, and whether they ever answered.
   *
   * Written once, by `LeadService.ensureFromFirstTouch`, and never
   * rewritten: a lead that replies, signs up and buys still has exactly
   * one first touch, and that is the row attribution reports group by.
   * The one exception is `repliedAt`, which is filled in the first time
   * they answer an outbound.
   */
  firstTouch: leadFirstTouchSchema.optional(),
  /**
   * The most recent contact, in either direction.
   *
   * The counterpart to {@link firstTouch}, and the reason it is stored
   * rather than derived: "who spoke last, and when" is what a rep sorts
   * and filters a queue by — a lead we messaged three days ago with no
   * answer is a different job from one that messaged us an hour ago —
   * and deriving it means scanning `timeline[]` on every row of the
   * list. The timeline stays the audit trail; this is the index.
   *
   * Overwritten on every touch, unlike `firstTouch`, which is written
   * once. On a brand-new lead both hold the same event.
   */
  lastTouch: leadLastTouchSchema.optional(),
  /**
   * The latest invoice raised for this lead.
   *
   * A pointer, not a history: `Billing.leadId` is the authoritative edge
   * and the CRM's Payments tab queries it for the full list. This exists
   * because "is there an invoice open on this lead" is asked in places
   * that hold a Lead and nothing else — the drawer header, and the
   * agent's "you have an unpaid invoice" reminder — and a snapshot ref
   * answers it without a second collection read.
   *
   * A ref rather than a bare `billingId` so the reference carries the
   * `main` number a human recognises. Replaced, never appended: the
   * second invoice supersedes the first as "the latest", and anything
   * that needs all of them has `Billing.leadId`.
   */
  billing: billingRefSchema.optional(),
  /**
   * Free-form attribution metadata. Carries UTM params, referrer,
   * landing path, and (server-stamped) `ip` + `userAgent` for leads
   * coming through the public endpoint.
   */
  sourceMeta: leadSourceMetaSchema.optional(),
});

export type LeadMeta = z.infer<typeof leadMetaSchema>;
